/* ============================================
   Los campos que agregó el partner, en la ficha

   La definición (etiqueta, tipo, opciones) vive en `campos_definicion` y el
   valor en `leads.campos`. Acá sólo se dibuja el control que pide cada tipo
   y se guarda con el mismo `PUT` que el resto de la ficha.

   Un campo que se quitó de la configuración pero tiene valor se sigue
   mostrando, en solo lectura: el dato lo escribió alguien y no desaparece
   porque hoy no esté en el formulario.
   ============================================ */
import { useEffect, useState } from 'react'
import { useApi } from '@/hooks/useApi'
import { showToast } from '@/components/layout/Toast'
import type { CampoDefinicion, Lead } from '@/lib/types'

interface Props {
  lead: Lead
  onRefresh: () => void
}

function comoTexto(valor: unknown, tipo: string): string {
  if (valor === null || valor === undefined || valor === '') return ''
  if (tipo === 'booleano') return valor ? 'Sí' : 'No'
  return String(valor)
}

export default function LeadCamposAMedida({ lead, onRefresh }: Props) {
  const api = useApi()
  const [defs, setDefs] = useState<CampoDefinicion[]>([])
  const [guardando, setGuardando] = useState<string | null>(null)

  useEffect(() => {
    let vivo = true
    api.get<CampoDefinicion[]>('/api/pipeline/campos')
      .then(r => { if (vivo) setDefs(Array.isArray(r) ? r : []) })
      .catch(() => { /* sin campos configurados no hay nada que dibujar */ })
    return () => { vivo = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const valores = (lead.campos || {}) as Record<string, unknown>
  const activos = defs.filter(d => d.activo)

  /* Lo que tiene valor y ya no está en el formulario: se muestra apagado en
     vez de perderse de vista. */
  const retirados = defs.filter(d => !d.activo && valores[d.clave] !== undefined)

  async function guardar(clave: string, valor: string | number | boolean | null) {
    setGuardando(clave)
    try {
      await api.put(`/api/leads/${lead.id}`, { campos: { [clave]: valor } })
      onRefresh()
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'No se pudo guardar', 'error')
    } finally {
      setGuardando(null)
    }
  }

  if (activos.length === 0 && retirados.length === 0) return null

  return (
    <>
      {activos.map(def => {
        const valor = valores[def.clave]
        const deshabilitado = guardando === def.clave
        return (
          <div className="ld-dato" key={def.id}>
            <span className="ld-dato__label">{def.etiqueta}</span>
            {def.tipo === 'lista' ? (
              <select
                className="form-select ld-dato__servicio"
                value={valor == null ? '' : String(valor)}
                onChange={e => guardar(def.clave, e.target.value || null)}
                disabled={deshabilitado}
                aria-label={def.etiqueta}
              >
                <option value="">— Sin definir —</option>
                {(def.opciones || []).map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : def.tipo === 'booleano' ? (
              <label className="ld-dato__check">
                <input
                  type="checkbox"
                  checked={valor === true}
                  onChange={e => guardar(def.clave, e.target.checked)}
                  disabled={deshabilitado}
                />
                <span>{valor === true ? 'Sí' : 'No'}</span>
              </label>
            ) : (
              <input
                className="form-input ld-dato__input"
                type={def.tipo === 'fecha' ? 'date' : (def.tipo === 'numero' || def.tipo === 'moneda') ? 'number' : 'text'}
                step={def.tipo === 'moneda' ? '0.01' : undefined}
                defaultValue={valor == null ? '' : String(valor)}
                placeholder="Sin definir"
                disabled={deshabilitado}
                aria-label={def.etiqueta}
                onBlur={e => {
                  const nuevo = e.target.value
                  if (nuevo === (valor == null ? '' : String(valor))) return
                  guardar(def.clave, nuevo === '' ? null : nuevo)
                }}
                onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
              />
            )}
          </div>
        )
      })}

      {retirados.map(def => (
        <div className="ld-dato ld-dato--retirado" key={def.id}>
          <span className="ld-dato__label">{def.etiqueta}</span>
          <span className="ld-dato__valor">{comoTexto(valores[def.clave], def.tipo)}</span>
        </div>
      ))}
    </>
  )
}
